import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { sampleReceiptsApi, type SampleReceiptRow } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import { useLaboratoryTimeZone } from '../../app/laboratory-time-zone.js';
import {
  ActionButton,
  DataTable,
  FilterBar,
  PageHeader,
  Panel,
  SearchField,
  StatusBadge,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDateTime, formatNumber } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { recordLinkClass } from './RecordHeader.js';

/**
 * The Sample Receipts register (D213).
 *
 * Every handover the laboratory recorded, newest first, so a receipt stays
 * reachable long after the receiving desk moved on. Read-only: a receipt is
 * never edited, and a sample voided since is counted, never removed.
 *
 * The search is the server's: receipt number, a Sample ID on the receipt, the
 * work-order number or the client's name — so a scanned sample label finds the
 * handover it arrived in.
 */
export function SampleReceiptsPage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const timeZone = useLaboratoryTimeZone();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const { can } = useAuth();
  const navigate = useNavigate();
  const mayViewWorkOrders = can(PERMISSIONS.WORK_ORDERS_VIEW);

  const receipts = useQuery({
    queryKey: ['sample-receipts', { page, search }],
    queryFn: () => sampleReceiptsApi.list({ page, ...(search ? { search } : {}) }),
  });

  const moment = (iso: string) =>
    formatDateTime(iso, { locale, ...(timeZone ? { timezone: timeZone } : {}) });

  const columns: readonly DataColumn<SampleReceiptRow>[] = [
    {
      key: 'receiptNumber',
      header: t('receiving.receiptNo'),
      render: (row) => (
        <Link
          to={`/samples/receipts/${row.id}`}
          className="measure font-semibold text-default-900 underline-offset-2 hover:text-primary-700 hover:underline"
          dir="ltr"
        >
          {row.receiptNumber}
        </Link>
      ),
    },
    {
      key: 'receivedAt',
      header: t('receiving.receiptDateTime'),
      render: (row) => <span className="text-default-700">{moment(row.receivedAt)}</span>,
    },
    {
      key: 'workOrder',
      header: t('samples.jobNumber'),
      render: (row) =>
        row.workOrder ? (
          mayViewWorkOrders ? (
            <Link
              to={`/work-orders/${row.workOrder.id}`}
              className={`measure ${recordLinkClass}`}
              dir="ltr"
            >
              {row.workOrder.workOrderNumber}
            </Link>
          ) : (
            <span className="measure text-default-700" dir="ltr">
              {row.workOrder.workOrderNumber}
            </span>
          )
        ) : (
          <span className="text-caption text-default-600">{t('samples.noWorkOrder')}</span>
        ),
    },
    {
      key: 'client',
      header: t('receiving.client'),
      render: (row) => (
        <div className="min-w-0">
          <span className="block text-default-800">
            {row.client?.name ?? t('common.notRecorded')}
          </span>
          {row.project ? (
            <span className="block truncate text-caption text-default-600">{row.project.name}</span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'samples',
      header: t('receipts.sampleCount'),
      align: 'end',
      render: (row) => (
        <div className="flex flex-col items-end gap-1">
          <span className="text-default-800" dir="ltr">
            {formatNumber(row.sampleCount, { locale })}
          </span>
          {row.voidedCount > 0 ? (
            <StatusBadge
              label={t('receipts.voidedCount', { count: row.voidedCount })}
              tone="warning"
              size="sm"
            />
          ) : null}
        </div>
      ),
    },
    {
      key: 'people',
      header: t('receipts.deliveredReceived'),
      render: (row) => (
        <div className="min-w-0">
          <span className="block truncate text-default-700">{row.receivedFrom}</span>
          <span className="block truncate text-caption text-default-600">
            {row.receivedBy.displayName ?? t('common.notRecorded')}
          </span>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('receipts.title')}
        subtitle={t('receipts.subtitle')}
        breadcrumbs={[{ label: t('nav.samples'), to: '/samples' }, { label: t('receipts.title') }]}
        actions={
          can(PERMISSIONS.SAMPLES_RECEIVE) ? (
            <ActionButton priority="primary" onPress={() => navigate('/samples/receive')}>
              {t('samples.receive')}
            </ActionButton>
          ) : null
        }
      />
      <FilterBar>
        <SearchField
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          label={t('receipts.searchLabel')}
          placeholder={t('receipts.searchPlaceholder')}
        />
      </FilterBar>
      <Panel>
        <DataTable
          label={t('receipts.title')}
        columns={columns}
        rows={receipts.data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={receipts.isLoading}
        error={receipts.error}
        onRetry={() => void receipts.refetch()}
        emptyContent={search ? t('receipts.noneFound') : t('receipts.noneYet')}
        page={page}
        totalPages={receipts.data?.meta.totalPages ?? 1}
          onPageChange={setPage}
        />
      </Panel>
    </>
  );
}
